// src/components/AdminDashboard.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdminAuth } from '../context/AdminAuthContext';
import axios from 'axios';
import '../styles/AdminDashboard.css';
import { API_BASE_URL } from '../utils/axiosConfig';

const API_URL = API_BASE_URL;

type Tab = 'overview' | 'users' | 'subscriptions' | 'ads' | 'community' | 'logs' | 'automations';

// Axios instance for admin API calls
const adminAxios = () => {
  const token = localStorage.getItem('adminToken');
  return axios.create({
    baseURL: `${API_URL}/admin`,
    headers: { Authorization: `Bearer ${token}` },
  });
};

// ============================================================
// OVERVIEW TAB
// ============================================================
interface DashboardData {
  overview: {
    totalUsers: number;
    newUsersThisMonth: number;
    newUsersThisWeek: number;
    activeSubscriptions: number;
    totalApplications: number;
    totalDiscussions: number;
    totalResumes: number;
    totalRevenue: number;
  };
  usersByRole: { role: string; count: number }[];
  signupTrend: { date: string; count: number }[];
}

const OverviewTab: React.FC = () => {
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    adminAxios()
      .get('/dashboard')
      .then((res) => setData(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="admin-loading">Loading dashboard...</div>;
  if (!data) return <div className="admin-error">Failed to load dashboard</div>;

  const { overview } = data;

  return (
    <div className="admin-tab-content">
      <h2>System Overview</h2>
      <div className="admin-stats-grid">
        <div className="admin-stat-card">
          <div className="stat-number">{overview.totalUsers}</div>
          <div className="stat-label">Total Users</div>
        </div>
        <div className="admin-stat-card accent-green">
          <div className="stat-number">{overview.newUsersThisWeek}</div>
          <div className="stat-label">New This Week</div>
        </div>
        <div className="admin-stat-card accent-blue">
          <div className="stat-number">{overview.activeSubscriptions}</div>
          <div className="stat-label">Active Subscriptions</div>
        </div>
        <div className="admin-stat-card accent-purple">
          <div className="stat-number">{overview.totalApplications}</div>
          <div className="stat-label">Total Applications</div>
        </div>
        <div className="admin-stat-card accent-orange">
          <div className="stat-number">{overview.totalDiscussions}</div>
          <div className="stat-label">Discussions</div>
        </div>
        <div className="admin-stat-card accent-teal">
          <div className="stat-number">{overview.totalResumes}</div>
          <div className="stat-label">Resumes</div>
        </div>
      </div>

      <div className="admin-section-row">
        <div className="admin-section-box">
          <h3>Users by Role</h3>
          <div className="role-list">
            {data.usersByRole.map((r) => (
              <div key={r.role} className="role-item">
                <span className={`role-badge role-${r.role}`}>{r.role}</span>
                <span className="role-count">{r.count}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="admin-section-box">
          <h3>Signup Trend (30 days)</h3>
          <div className="mini-chart">
            {data.signupTrend.map((d, i) => (
              <div
                key={i}
                className="chart-bar"
                title={`${d.date}: ${d.count}`}
                style={{
                  height: `${Math.max(4, (d.count / Math.max(...data.signupTrend.map((s) => s.count), 1)) * 80)}px`,
                }}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================================
// USERS TAB
// ============================================================
interface UserListItem {
  id: string;
  email: string;
  fullName: string;
  role: string;
  status: string;
  createdAt: string;
  mfaEnabled: boolean;
  _count: { applications: number; resumes: number; discussions: number };
}

interface UserDetail {
  id: string;
  email: string;
  fullName: string;
  phone: string | null;
  role: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  mfaEnabled: boolean;
  experienceLevel: string | null;
  industries: string | null;
  preferredJobTitles: string | null;
  failedLoginAttempts: number;
  lockoutUntil: string | null;
  _count: Record<string, number>;
  subscriptions: any[];
  consents: any[];
}

const UsersTab: React.FC = () => {
  const [users, setUsers] = useState<UserListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState<UserDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [actionMsg, setActionMsg] = useState('');

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminAxios().get('/users', {
        params: { page, search, role: roleFilter || undefined, limit: 20 },
      });
      setUsers(res.data.users);
      setTotal(res.data.total);
    } catch {
    } finally {
      setLoading(false);
    }
  }, [page, search, roleFilter]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const viewUser = async (id: string) => {
    setDetailLoading(true);
    try {
      const res = await adminAxios().get(`/users/${id}`);
      setSelectedUser(res.data.user);
    } catch {
    } finally {
      setDetailLoading(false);
    }
  };

  const changeRole = async (userId: string, newRole: string) => {
    try {
      await adminAxios().put(`/users/${userId}/role`, { role: newRole });
      setActionMsg(`Role updated to ${newRole}`);
      fetchUsers();
      if (selectedUser?.id === userId) viewUser(userId);
      setTimeout(() => setActionMsg(''), 3000);
    } catch (err: any) {
      setActionMsg(err.response?.data?.error || 'Failed to update role');
      setTimeout(() => setActionMsg(''), 3000);
    }
  };

  const changeStatus = async (userId: string, newStatus: string) => {
    try {
      await adminAxios().put(`/users/${userId}/status`, { status: newStatus });
      setActionMsg(`Status updated to ${newStatus}`);
      fetchUsers();
      if (selectedUser?.id === userId) viewUser(userId);
      setTimeout(() => setActionMsg(''), 3000);
    } catch (err: any) {
      setActionMsg(err.response?.data?.error || 'Failed to update status');
      setTimeout(() => setActionMsg(''), 3000);
    }
  };

  const deleteUser = async (userId: string, email: string) => {
    if (!window.confirm(`Delete user ${email}? This action is irreversible.`)) return;
    try {
      await adminAxios().delete(`/users/${userId}`);
      setActionMsg('User deleted');
      setSelectedUser(null);
      fetchUsers();
      setTimeout(() => setActionMsg(''), 3000);
    } catch (err: any) {
      setActionMsg(err.response?.data?.error || 'Failed to delete user');
      setTimeout(() => setActionMsg(''), 3000);
    }
  };

  const totalPages = Math.ceil(total / 20);

  return (
    <div className="admin-tab-content">
      <h2>User Management ({total} total)</h2>

      {actionMsg && <div className="admin-action-msg">{actionMsg}</div>}

      {/* Filters */}
      <div className="admin-filters">
        <input
          type="text"
          placeholder="Search by name or email..."
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(0); }}
          className="admin-search-input"
        />
        <select
          value={roleFilter}
          onChange={(e) => { setRoleFilter(e.target.value); setPage(0); }}
          className="admin-select"
        >
          <option value="">All Roles</option>
          <option value="user">User</option>
          <option value="admin">Admin</option>
          <option value="super_admin">Super Admin</option>
        </select>
      </div>

      {/* User detail modal */}
      {selectedUser && (
        <div className="admin-user-detail-overlay" onClick={() => setSelectedUser(null)}>
          <div className="admin-user-detail" onClick={(e) => e.stopPropagation()}>
            <button className="admin-close-btn" onClick={() => setSelectedUser(null)}>×</button>
            {detailLoading ? (
              <div className="admin-loading">Loading...</div>
            ) : (
              <>
                <h3>{selectedUser.fullName || 'No Name'}</h3>
                <p className="user-detail-email">{selectedUser.email}</p>

                <div className="user-detail-grid">
                  <div><strong>Role:</strong> <span className={`role-badge role-${selectedUser.role}`}>{selectedUser.role}</span></div>
                  <div><strong>Status:</strong> <span className={`status-badge status-${selectedUser.status}`}>{selectedUser.status}</span></div>
                  <div><strong>MFA:</strong> {selectedUser.mfaEnabled ? '✅ Enabled' : '❌ Disabled'}</div>
                  <div><strong>Joined:</strong> {new Date(selectedUser.createdAt).toLocaleDateString()}</div>
                  {selectedUser.phone && <div><strong>Phone:</strong> {selectedUser.phone}</div>}
                  {selectedUser.experienceLevel && <div><strong>Experience:</strong> {selectedUser.experienceLevel}</div>}
                  <div><strong>Failed Logins:</strong> {selectedUser.failedLoginAttempts}</div>
                  {selectedUser.lockoutUntil && (
                    <div><strong>Locked Until:</strong> {new Date(selectedUser.lockoutUntil).toLocaleString()}</div>
                  )}
                </div>

                <div className="user-detail-counts">
                  {Object.entries(selectedUser._count).map(([key, val]) => (
                    <div key={key} className="count-item">
                      <span className="count-val">{val}</span>
                      <span className="count-label">{key.replace(/([A-Z])/g, ' $1').trim()}</span>
                    </div>
                  ))}
                </div>

                <div className="user-detail-actions">
                  <div className="action-group">
                    <label>Change Role:</label>
                    <div className="action-buttons">
                      {['user', 'admin', 'super_admin'].map((r) => (
                        <button
                          key={r}
                          className={`action-btn ${r === selectedUser.role ? 'active' : ''}`}
                          onClick={() => changeRole(selectedUser.id, r)}
                          disabled={r === selectedUser.role}
                        >
                          {r}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="action-group">
                    <label>Change Status:</label>
                    <div className="action-buttons">
                      {['Active', 'Suspended'].map((s) => (
                        <button
                          key={s}
                          className={`action-btn ${s === selectedUser.status ? 'active' : ''}`}
                          onClick={() => changeStatus(selectedUser.id, s)}
                          disabled={s === selectedUser.status}
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>
                  <button
                    className="action-btn danger"
                    onClick={() => deleteUser(selectedUser.id, selectedUser.email)}
                  >
                    🗑️ Delete User
                  </button>
                </div>

                {selectedUser.subscriptions.length > 0 && (
                  <div className="user-detail-section">
                    <h4>Recent Subscriptions</h4>
                    {selectedUser.subscriptions.map((sub: any) => (
                      <div key={sub.id} className="sub-item">
                        {sub.plan?.name} — {sub.status}
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Users table */}
      {loading ? (
        <div className="admin-loading">Loading users...</div>
      ) : (
        <>
          <div className="admin-table-wrapper">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Joined</th>
                  <th>Apps</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id}>
                    <td>{u.fullName || '—'}</td>
                    <td>{u.email}</td>
                    <td><span className={`role-badge role-${u.role}`}>{u.role}</span></td>
                    <td><span className={`status-badge status-${u.status}`}>{u.status}</span></td>
                    <td>{new Date(u.createdAt).toLocaleDateString()}</td>
                    <td>{u._count.applications}</td>
                    <td>
                      <button className="admin-view-btn" onClick={() => viewUser(u.id)}>View</button>
                    </td>
                  </tr>
                ))}
                {users.length === 0 && (
                  <tr><td colSpan={7} className="no-data">No users found</td></tr>
                )}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="admin-pagination">
              <button disabled={page === 0} onClick={() => setPage(page - 1)}>← Prev</button>
              <span>Page {page + 1} of {totalPages}</span>
              <button disabled={page >= totalPages - 1} onClick={() => setPage(page + 1)}>Next →</button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// ============================================================
// SUBSCRIPTIONS TAB
// ============================================================
const SubscriptionsTab: React.FC = () => {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    adminAxios()
      .get('/subscriptions')
      .then((res) => setData(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="admin-loading">Loading subscriptions...</div>;
  if (!data) return <div className="admin-error">Failed to load subscriptions</div>;

  return (
    <div className="admin-tab-content">
      <h2>Subscription Management</h2>

      <div className="admin-stats-grid">
        <div className="admin-stat-card accent-green">
          <div className="stat-number">${(data.totalRevenue / 100).toFixed(2)}</div>
          <div className="stat-label">Total Revenue</div>
        </div>
        <div className="admin-stat-card accent-blue">
          <div className="stat-number">{data.activeSubscriptions?.length || 0}</div>
          <div className="stat-label">Active Subscriptions</div>
        </div>
      </div>

      <h3>Plans</h3>
      <div className="admin-table-wrapper">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Plan</th>
              <th>Price</th>
              <th>Subscribers</th>
            </tr>
          </thead>
          <tbody>
            {data.plans?.map((p: any) => (
              <tr key={p.id}>
                <td>{p.name}</td>
                <td>${(p.price / 100).toFixed(2)}/mo</td>
                <td>{p._count?.subscriptions || 0}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h3>Recent Payments</h3>
      <div className="admin-table-wrapper">
        <table className="admin-table">
          <thead>
            <tr>
              <th>User</th>
              <th>Amount</th>
              <th>Status</th>
              <th>Date</th>
            </tr>
          </thead>
          <tbody>
            {data.recentPayments?.map((p: any) => (
              <tr key={p.id}>
                <td>{p.user?.fullName || p.user?.email || '—'}</td>
                <td>${(p.amount / 100).toFixed(2)}</td>
                <td>{p.status}</td>
                <td>{new Date(p.createdAt).toLocaleDateString()}</td>
              </tr>
            ))}
            {(!data.recentPayments || data.recentPayments.length === 0) && (
              <tr><td colSpan={4} className="no-data">No payments yet</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ============================================================
// ADS TAB
// ============================================================
const AdsTab: React.FC = () => {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', imageUrl: '', targetUrl: '', position: 'sidebar', tier: 'free' });
  const [actionMsg, setActionMsg] = useState('');

  const fetchAds = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminAxios().get('/ads');
      setData(res.data);
    } catch {
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAds();
  }, [fetchAds]);

  const createAd = async () => {
    try {
      await adminAxios().post('/ads', form);
      setActionMsg('Ad created!');
      setShowCreate(false);
      setForm({ title: '', description: '', imageUrl: '', targetUrl: '', position: 'sidebar', tier: 'free' });
      fetchAds();
      setTimeout(() => setActionMsg(''), 3000);
    } catch {
      setActionMsg('Failed to create ad');
      setTimeout(() => setActionMsg(''), 3000);
    }
  };

  const toggleAd = async (id: string, isActive: boolean) => {
    try {
      await adminAxios().put(`/ads/${id}`, { isActive: !isActive });
      fetchAds();
    } catch {}
  };

  const deleteAd = async (id: string) => {
    if (!window.confirm('Delete this ad?')) return;
    try {
      await adminAxios().delete(`/ads/${id}`);
      fetchAds();
    } catch {}
  };

  if (loading) return <div className="admin-loading">Loading ads...</div>;
  if (!data) return <div className="admin-error">Failed to load ads</div>;

  return (
    <div className="admin-tab-content">
      <div className="admin-tab-header">
        <h2>Ad Management</h2>
        <button className="admin-primary-btn" onClick={() => setShowCreate(!showCreate)}>
          {showCreate ? 'Cancel' : '+ New Ad'}
        </button>
      </div>

      {actionMsg && <div className="admin-action-msg">{actionMsg}</div>}

      <div className="admin-stats-grid">
        <div className="admin-stat-card">
          <div className="stat-number">{data.summary.totalAds}</div>
          <div className="stat-label">Total Ads</div>
        </div>
        <div className="admin-stat-card accent-green">
          <div className="stat-number">{data.summary.activeAds}</div>
          <div className="stat-label">Active</div>
        </div>
        <div className="admin-stat-card accent-blue">
          <div className="stat-number">{data.summary.totalImpressions}</div>
          <div className="stat-label">Impressions</div>
        </div>
        <div className="admin-stat-card accent-purple">
          <div className="stat-number">{data.summary.avgCtr}%</div>
          <div className="stat-label">Avg CTR</div>
        </div>
      </div>

      {showCreate && (
        <div className="admin-create-form">
          <h3>Create New Ad</h3>
          <div className="admin-form-row">
            <input placeholder="Title *" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
            <input placeholder="Target URL *" value={form.targetUrl} onChange={(e) => setForm({ ...form, targetUrl: e.target.value })} />
          </div>
          <div className="admin-form-row">
            <input placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            <input placeholder="Image URL" value={form.imageUrl} onChange={(e) => setForm({ ...form, imageUrl: e.target.value })} />
          </div>
          <div className="admin-form-row">
            <select value={form.position} onChange={(e) => setForm({ ...form, position: e.target.value })}>
              <option value="sidebar">Sidebar</option>
              <option value="banner">Banner</option>
              <option value="inline">Inline</option>
            </select>
            <select value={form.tier} onChange={(e) => setForm({ ...form, tier: e.target.value })}>
              <option value="free">Free</option>
              <option value="pro">Pro</option>
              <option value="premium">Premium</option>
            </select>
            <button className="admin-primary-btn" onClick={createAd} disabled={!form.title || !form.targetUrl}>
              Create
            </button>
          </div>
        </div>
      )}

      <div className="admin-table-wrapper">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Title</th>
              <th>Position</th>
              <th>Tier</th>
              <th>Impressions</th>
              <th>Clicks</th>
              <th>Active</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {data.ads.map((ad: any) => (
              <tr key={ad.id}>
                <td>{ad.title}</td>
                <td>{ad.position}</td>
                <td>{ad.tier}</td>
                <td>{ad.impressions}</td>
                <td>{ad.clicks}</td>
                <td>
                  <button
                    className={`toggle-btn ${ad.isActive ? 'active' : 'inactive'}`}
                    onClick={() => toggleAd(ad.id, ad.isActive)}
                  >
                    {ad.isActive ? 'ON' : 'OFF'}
                  </button>
                </td>
                <td>
                  <button className="admin-danger-btn-sm" onClick={() => deleteAd(ad.id)}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ============================================================
// COMMUNITY TAB
// ============================================================
const CommunityTab: React.FC = () => {
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    adminAxios()
      .get('/community/stats')
      .then((res) => setStats(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="admin-loading">Loading community stats...</div>;
  if (!stats) return <div className="admin-error">Failed to load community stats</div>;

  return (
    <div className="admin-tab-content">
      <h2>Community Overview</h2>
      <div className="admin-stats-grid">
        <div className="admin-stat-card">
          <div className="stat-number">{stats.discussions}</div>
          <div className="stat-label">Discussions</div>
        </div>
        <div className="admin-stat-card accent-blue">
          <div className="stat-number">{stats.replies}</div>
          <div className="stat-label">Replies</div>
        </div>
        <div className="admin-stat-card accent-green">
          <div className="stat-number">{stats.publicProfiles}</div>
          <div className="stat-label">Public Profiles</div>
        </div>
        <div className="admin-stat-card accent-purple">
          <div className="stat-number">{stats.connections}</div>
          <div className="stat-label">Connections</div>
        </div>
      </div>
      <p className="admin-info-text">
        Use the Community section (user-facing) to browse discussions. Admins can delete
        specific discussions or replies via the API if they violate community guidelines.
      </p>
    </div>
  );
};

// ============================================================
// LOGS TAB (super_admin only)
// ============================================================
const LogsTab: React.FC = () => {
  const [logs, setLogs] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { admin } = useAdminAuth();

  useEffect(() => {
    setLoading(true);
    adminAxios()
      .get('/audit-logs', { params: { page, limit: 30 } })
      .then((res) => {
        setLogs(res.data.logs);
        setTotal(res.data.total);
      })
      .catch((err) => {
        if (err.response?.status === 403) {
          setError('Super Admin access required for audit logs.');
        } else {
          setError('Failed to load audit logs.');
        }
      })
      .finally(() => setLoading(false));
  }, [page]);

  if (admin?.role !== 'super_admin') {
    return (
      <div className="admin-tab-content">
        <h2>Audit Logs</h2>
        <div className="admin-error">This section requires Super Admin access.</div>
      </div>
    );
  }

  if (loading) return <div className="admin-loading">Loading logs...</div>;
  if (error) return <div className="admin-tab-content"><h2>Audit Logs</h2><div className="admin-error">{error}</div></div>;

  const totalPages = Math.ceil(total / 30);

  return (
    <div className="admin-tab-content">
      <h2>Audit Logs ({total} total)</h2>
      <div className="admin-table-wrapper">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Timestamp</th>
              <th>User</th>
              <th>Action</th>
              <th>Details</th>
            </tr>
          </thead>
          <tbody>
            {logs.map((log: any) => (
              <tr key={log.id}>
                <td>{new Date(log.createdAt).toLocaleString()}</td>
                <td>{log.user?.email || '—'}</td>
                <td>{log.action}</td>
                <td className="log-details">{log.details ? JSON.stringify(log.details).substring(0, 80) : '—'}</td>
              </tr>
            ))}
            {logs.length === 0 && (
              <tr><td colSpan={4} className="no-data">No audit logs yet</td></tr>
            )}
          </tbody>
        </table>
      </div>
      {totalPages > 1 && (
        <div className="admin-pagination">
          <button disabled={page === 0} onClick={() => setPage(page - 1)}>← Prev</button>
          <span>Page {page + 1} of {totalPages}</span>
          <button disabled={page >= totalPages - 1} onClick={() => setPage(page + 1)}>Next →</button>
        </div>
      )}
    </div>
  );
};

// ============================================================
// AUTOMATIONS TAB (Phase 12.7)
// ============================================================
interface SystemEvent {
  id: string;
  eventType: string;
  actorType: string;
  actorId: string | null;
  severity: string;
  message: string;
  affectedIds: string[];
  undoExpiresAt: string | null;
  undoneAt: string | null;
  createdAt: string;
}

interface StatusRule {
  id: string;
  name: string;
  enabled: boolean;
  fromStatus: string;
  toStatus: string;
  afterDays: number;
  description: string | null;
}

const AutomationsTab: React.FC = () => {
  const [events, setEvents] = useState<SystemEvent[]>([]);
  const [rules, setRules] = useState<StatusRule[]>([]);
  const [severityFilter, setSeverityFilter] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [showAddRule, setShowAddRule] = useState(false);
  const [newRule, setNewRule] = useState<Partial<StatusRule>>({
    name: '',
    fromStatus: 'interview_scheduled',
    toStatus: 'rejected',
    afterDays: 30,
  });

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const api = adminAxios();
      const [evRes, ruleRes] = await Promise.all([
        api.get(`/automation/events${severityFilter ? `?severity=${severityFilter}` : ''}`),
        api.get('/automation/rules'),
      ]);
      setEvents(evRes.data.events || []);
      setRules(ruleRes.data.rules || []);
    } catch (err) {
      console.error('Automations load failed:', err);
    } finally {
      setLoading(false);
    }
  }, [severityFilter]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const undoEvent = async (id: string) => {
    if (!window.confirm('Undo this automated action? This will restore the affected records.')) return;
    try {
      await adminAxios().post(`/automation/events/${id}/undo`);
      fetchAll();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Undo failed');
    }
  };

  const runSweep = async (type: 'delete' | 'status_rules') => {
    if (!window.confirm(`Manually trigger the ${type} sweep now?`)) return;
    try {
      const res = await adminAxios().post(`/automation/run/${type}`);
      alert(`Sweep complete: ${res.data.count} record(s) affected`);
      fetchAll();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Sweep failed');
    }
  };

  const addRule = async () => {
    if (!newRule.name || !newRule.fromStatus || !newRule.toStatus || !newRule.afterDays) {
      alert('All fields required'); return;
    }
    try {
      await adminAxios().post('/automation/rules', newRule);
      setShowAddRule(false);
      setNewRule({ name: '', fromStatus: 'interview_scheduled', toStatus: 'rejected', afterDays: 30 });
      fetchAll();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to add rule');
    }
  };

  const deleteRule = async (id: string) => {
    if (!window.confirm('Delete this rule?')) return;
    try {
      await adminAxios().delete(`/automation/rules/${id}`);
      fetchAll();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Delete failed');
    }
  };

  const severityColor = (s: string) =>
    s === 'critical' ? '#dc2626' : s === 'warning' ? '#d97706' : '#16a34a';

  const undoableNow = (ev: SystemEvent) =>
    !ev.undoneAt && ev.undoExpiresAt && new Date(ev.undoExpiresAt) > new Date();

  return (
    <div>
      {/* Manual sweep buttons */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        <button onClick={() => runSweep('delete')} className="admin-btn">▶ Run expired-cleanup sweep</button>
        <button onClick={() => runSweep('status_rules')} className="admin-btn">▶ Run status-rules sweep</button>
        <button onClick={() => setShowAddRule(true)} className="admin-btn">+ Add status rule</button>
        <select value={severityFilter} onChange={(e) => setSeverityFilter(e.target.value)} style={{ marginLeft: 'auto', padding: '6px 10px' }}>
          <option value="">All severities</option>
          <option value="info">Info</option>
          <option value="warning">Warning</option>
          <option value="critical">Critical</option>
        </select>
      </div>

      {/* Status rules list */}
      <h3 style={{ marginTop: 0 }}>Status-transition rules</h3>
      {rules.length === 0 ? (
        <p style={{ color: '#64748b' }}>No rules defined.</p>
      ) : (
        <table className="admin-table" style={{ marginBottom: 24 }}>
          <thead>
            <tr><th>Name</th><th>From</th><th>To</th><th>After</th><th>Enabled</th><th></th></tr>
          </thead>
          <tbody>
            {rules.map((r) => (
              <tr key={r.id}>
                <td>{r.name}</td>
                <td>{r.fromStatus}</td>
                <td>{r.toStatus}</td>
                <td>{r.afterDays}d</td>
                <td>{r.enabled ? '✓' : '✗'}</td>
                <td><button onClick={() => deleteRule(r.id)} className="admin-btn-danger">Delete</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Event feed */}
      <h3>System events {loading && <span style={{ fontSize: 12, color: '#64748b' }}>(loading...)</span>}</h3>
      <div style={{ maxHeight: 600, overflow: 'auto', border: '1px solid #e2e8f0', borderRadius: 8 }}>
        {events.length === 0 ? (
          <p style={{ padding: 24, color: '#64748b', textAlign: 'center' }}>No events recorded yet.</p>
        ) : (
          events.map((ev) => (
            <div key={ev.id} style={{ padding: 12, borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', gap: 12 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4 }}>
                  <span style={{ background: severityColor(ev.severity), color: '#fff', fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 4, textTransform: 'uppercase' }}>
                    {ev.severity}
                  </span>
                  <span style={{ fontSize: 12, color: '#64748b' }}>{ev.eventType}</span>
                  <span style={{ fontSize: 12, color: '#94a3b8' }}>· {new Date(ev.createdAt).toLocaleString()}</span>
                  {ev.actorType === 'admin' && <span style={{ fontSize: 11, color: '#6366f1' }}>(admin action)</span>}
                </div>
                <div style={{ fontSize: 14 }}>{ev.message}</div>
                {ev.affectedIds.length > 0 && (
                  <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>
                    {ev.affectedIds.length} affected record(s)
                  </div>
                )}
                {ev.undoneAt && (
                  <div style={{ fontSize: 11, color: '#16a34a', marginTop: 2 }}>↶ Undone at {new Date(ev.undoneAt).toLocaleString()}</div>
                )}
              </div>
              {undoableNow(ev) && (
                <button onClick={() => undoEvent(ev.id)} className="admin-btn" style={{ alignSelf: 'flex-start' }}>
                  ↶ Undo
                </button>
              )}
            </div>
          ))
        )}
      </div>

      {/* Add-rule modal */}
      {showAddRule && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', padding: 24, borderRadius: 8, maxWidth: 440, width: '90%' }}>
            <h3 style={{ marginTop: 0 }}>Add status-transition rule</h3>
            <label style={{ display: 'block', marginBottom: 12 }}>
              Name <input type="text" value={newRule.name || ''} onChange={(e) => setNewRule({ ...newRule, name: e.target.value })} style={{ display: 'block', width: '100%', padding: 6, marginTop: 4 }} />
            </label>
            <label style={{ display: 'block', marginBottom: 12 }}>
              From status <input type="text" value={newRule.fromStatus || ''} onChange={(e) => setNewRule({ ...newRule, fromStatus: e.target.value })} style={{ display: 'block', width: '100%', padding: 6, marginTop: 4 }} />
            </label>
            <label style={{ display: 'block', marginBottom: 12 }}>
              To status <input type="text" value={newRule.toStatus || ''} onChange={(e) => setNewRule({ ...newRule, toStatus: e.target.value })} style={{ display: 'block', width: '100%', padding: 6, marginTop: 4 }} />
            </label>
            <label style={{ display: 'block', marginBottom: 16 }}>
              After (days) <input type="number" value={newRule.afterDays || 0} onChange={(e) => setNewRule({ ...newRule, afterDays: parseInt(e.target.value, 10) })} style={{ display: 'block', width: '100%', padding: 6, marginTop: 4 }} />
            </label>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => setShowAddRule(false)} className="admin-btn">Cancel</button>
              <button onClick={addRule} className="admin-btn-primary">Save rule</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================
// MAIN ADMIN DASHBOARD
// ============================================================
const AdminDashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState<Tab>('overview');
  const { admin, adminLogout } = useAdminAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    adminLogout();
    navigate('/admin/login');
  };

  const tabs: { key: Tab; label: string; icon: string }[] = [
    { key: 'overview', label: 'Overview', icon: '📊' },
    { key: 'users', label: 'Users', icon: '👥' },
    { key: 'subscriptions', label: 'Subscriptions', icon: '💳' },
    { key: 'automations', label: 'Automations', icon: '⚙️' },
    { key: 'ads', label: 'Ads', icon: '📢' },
    { key: 'community', label: 'Community', icon: '💬' },
    { key: 'logs', label: 'Audit Logs', icon: '📋' },
  ];

  return (
    <div className="admin-dashboard">
      {/* Sidebar */}
      <aside className="admin-sidebar">
        <div className="admin-sidebar-header">
          <div className="admin-logo">🛡️</div>
          <h2>Sumbitrbuddy</h2>
          <span className="admin-subtitle">Admin Panel</span>
        </div>

        <nav className="admin-nav">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              className={`admin-nav-item ${activeTab === tab.key ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.key)}
            >
              <span className="nav-icon">{tab.icon}</span>
              <span className="nav-label">{tab.label}</span>
            </button>
          ))}
        </nav>

        <div className="admin-sidebar-footer">
          <div className="admin-user-info">
            <span className="admin-user-name">{admin?.fullName}</span>
            <span className={`role-badge role-${admin?.role}`}>{admin?.role}</span>
          </div>
          <button className="admin-logout-btn" onClick={handleLogout}>
            Logout
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="admin-main">
        <header className="admin-header">
          <h1>{tabs.find((t) => t.key === activeTab)?.icon} {tabs.find((t) => t.key === activeTab)?.label}</h1>
          <span className="admin-header-role">
            Logged in as <strong>{admin?.email}</strong>
          </span>
        </header>

        <div className="admin-content">
          {activeTab === 'overview' && <OverviewTab />}
          {activeTab === 'users' && <UsersTab />}
          {activeTab === 'subscriptions' && <SubscriptionsTab />}
          {activeTab === 'automations' && <AutomationsTab />}
          {activeTab === 'ads' && <AdsTab />}
          {activeTab === 'community' && <CommunityTab />}
          {activeTab === 'logs' && <LogsTab />}
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;
